var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { MatDialog } from '@angular/material';
import { Router } from '@angular/router';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { of, Observable } from 'rxjs';
import { catchError, exhaustMap, map, tap, mergeMap, switchMap } from 'rxjs/operators';
import { LoginPageActions, AuthenticationActions, AuthenticationApiActions, RegisterUserPageActions, ForgotPasswordPageActions, } from '@app/auth/actions';
import { AuthService } from '@app/auth/services/auth.service';
import { LogoutConfirmationDialogComponent } from '@app/auth/components/logout-confirmation-dialog.component';
import { getWelcomePage } from '@app/auth/guards/welcome-guard.service';
import { LayoutActions } from '@app/core/actions';
export class AuthenticationEffects {
    constructor(actions$, authService, router, dialog) {
        this.actions$ = actions$;
        this.authService = authService;
        this.router = router;
        this.dialog = dialog;
        this.twoFactorUrl = "/twofactor";
        this.recoveryCodeUrl = "recoveryCode";
        this.forgotPasswordRequest$ = this.actions$.pipe(ofType(ForgotPasswordPageActions.RegisterForgotPasswordPageActionTypes.SubmitForgotPassword), map(action => action.payload), mergeMap((forgotPasswordRequest) => this.authService.forgotPasswordRequest(forgotPasswordRequest).pipe(map(response => new AuthenticationApiActions.ForgotPasswordRequestSuccess(response)), catchError(response => of(new AuthenticationApiActions.ForgotPasswordRequestFailure(response.error.Errors))))));
        this.resetPasswordRequest$ = this.actions$.pipe(ofType(ForgotPasswordPageActions.RegisterForgotPasswordPageActionTypes.SubmitResetPassword), map(action => action.payload), mergeMap((resetPasswordRequest) => this.authService.resetPasswordRequest(resetPasswordRequest).pipe(switchMap(response => [
            new AuthenticationApiActions.ResetPasswordRequestSuccess(response),
            new LayoutActions.OpenSuccessSnackBar({
                messages: ['Wachtwoord gewijzigd'],
            }),
        ]), tap(() => this.router.navigate(['/login'])), catchError(response => of(new AuthenticationApiActions.ResetPasswordRequestFailure(response.error.Errors))))));
        this.error$ = this.actions$.pipe(ofType(AuthenticationApiActions.AuthenticationApiActionTypes.ResetPasswordRequestFailure), map(action => action.payload), mergeMap((errorMessages) => {
            return of(new LayoutActions.OpenErrorSnackBar({ messages: errorMessages.map(message => message.Value) }));
        }));
        this.register$ = this.actions$.pipe(ofType(RegisterUserPageActions.RegisterUserPageActionTypes.SubmitRegisterUser), map(action => action.payload), mergeMap((registerUser) => this.authService.register(registerUser).pipe(map(response => new AuthenticationApiActions.RegisterUserSuccess(response)), tap(() => this.router.navigate(['/information'])), catchError(response => of(new AuthenticationApiActions.RegisterUserFailure(response.error.Errors))))));
        this.login$ = this.actions$.pipe(ofType(LoginPageActions.LoginPageActionTypes.Login), map(action => action.payload), exhaustMap((data) => this.authService.login(data.credentials).pipe(map(loginResponse => new AuthenticationApiActions.LoginSuccess({ loginResponse })), tap(loginSuccess => {
            if (loginSuccess.payload.loginResponse.isTwoFactorEnabled || loginSuccess.payload.loginResponse.twoFactorQrCode != null) {
                this.router.navigate([this.twoFactorUrl]);
            }
            else if (data.redirectURL) {
                this.router.navigate([data.redirectURL]);
            }
            else {
                this.router.navigate([getWelcomePage(loginSuccess.payload.loginResponse.isCustomer)]);
            }
        }), catchError(error => of(new AuthenticationApiActions.LoginFailure({ error }))))));
        this.twoFactorLogin$ = this.actions$.pipe(ofType(LoginPageActions.LoginPageActionTypes.TwoFactorLogin), map(action => action.payload), exhaustMap((data) => this.authService.twoFactorLogin(data.twoFactorRequest).pipe(map(loginResponse => new AuthenticationApiActions.LoginSuccess({ loginResponse })), tap(loginSuccess => {
            if (loginSuccess.payload.loginResponse.twoFactorRecoveryCode != null) {
                this.router.navigate([this.recoveryCodeUrl]);
            }
            else if (data.redirectURL) {
                this.router.navigate([data.redirectURL]);
            }
            else {
                this.router.navigate([getWelcomePage(loginSuccess.payload.loginResponse.isCustomer)]);
            }
        }), catchError(error => of(new AuthenticationApiActions.LoginFailure({ error }))))));
        this.recoverTwoFactorAuthentication$ = this.actions$.pipe(ofType(LoginPageActions.LoginPageActionTypes.RecoverTwoFactorAuthentication), map(action => action.payload), exhaustMap((data) => this.authService.recoverTwoFactor(data.code).pipe(map(loginResponse => new LoginPageActions.RecoverTwoFactorAuthenticationSuccess(loginResponse)), tap(loginSuccess => {
            this.router.navigate([data.redirectURL != null ? data.redirectURL : getWelcomePage(loginSuccess.payload.isCustomer)]);
        }), catchError(error => of(new AuthenticationApiActions.LoginFailure({ error }))))));
        this.sendTwoFactorAuthenticationCode$ = this.actions$.pipe(ofType(LoginPageActions.LoginPageActionTypes.SendTwoFactorActivationCode), map(action => action.payload), exhaustMap((payload) => this.authService.sendTwoFactorActivationCode(payload).pipe(map(() => new LoginPageActions.SendTwoFactorActivationCodeSuccess()), catchError(error => of(new LoginPageActions.SendTwoFactorActivationCodeFailure({ error }))))));
        this.loginRedirect$ = this.actions$.pipe(ofType(AuthenticationApiActions.AuthenticationApiActionTypes.LoginRedirect), map(action => action.payload), mergeMap((redirectURL) => this.router.navigate(['/login'], { queryParams: { redirectURL } })));
        this.logoutRedirect$ = this.actions$.pipe(ofType(AuthenticationActions.AuthenticationActionTypes.Logout), tap(() => {
            this.router.navigate(['/login']);
        }));
        this.logoutConfirmation$ = this.actions$.pipe(ofType(AuthenticationActions.AuthenticationActionTypes.LogoutConfirmation), exhaustMap(() => {
            const dialogRef = this.dialog.open(LogoutConfirmationDialogComponent);
            return dialogRef.afterClosed();
        }), map(result => (result ? new AuthenticationActions.Logout() : new AuthenticationActions.LogoutConfirmationDismiss())));
    }
}
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], AuthenticationEffects.prototype, "forgotPasswordRequest$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], AuthenticationEffects.prototype, "resetPasswordRequest$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], AuthenticationEffects.prototype, "error$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], AuthenticationEffects.prototype, "register$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Object)
], AuthenticationEffects.prototype, "login$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Object)
], AuthenticationEffects.prototype, "twoFactorLogin$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Object)
], AuthenticationEffects.prototype, "recoverTwoFactorAuthentication$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Object)
], AuthenticationEffects.prototype, "sendTwoFactorAuthenticationCode$", void 0);
__decorate([
    Effect({ dispatch: false }),
    __metadata("design:type", Object)
], AuthenticationEffects.prototype, "loginRedirect$", void 0);
__decorate([
    Effect({ dispatch: false }),
    __metadata("design:type", Object)
], AuthenticationEffects.prototype, "logoutRedirect$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Object)
], AuthenticationEffects.prototype, "logoutConfirmation$", void 0);
